import { ActionIcon, Group, Progress, Stack, Text, Tooltip } from '@mantine/core';
import { IconPlayerPause, IconPlayerPlay, IconRefresh, IconX } from '@tabler/icons-react';
import { formatBytes } from '@/lib/format';
import { useUploads, type UploadItem } from './context';

const PHASE: Record<UploadItem['phase'], string> = {
  creating: 'Preparing…',
  uploading: 'Uploading',
  paused: 'Paused',
  uploaded: 'Uploaded — encoding at Bunny',
  error: 'Upload failed',
};

export function UploadProgress({ upload }: { upload: UploadItem }) {
  const { pause, resume, dismiss } = useUploads();
  const pct = upload.size ? Math.min(100, Math.round((upload.bytesSent / upload.size) * 100)) : 0;
  const active = upload.phase === 'uploading' || upload.phase === 'creating';
  return (
    <Stack gap={4} data-testid="upload-progress">
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text size="sm" fw={500} truncate>
          {upload.title}
        </Text>
        <Group gap={2} wrap="nowrap">
          {upload.phase === 'uploading' && (
            <Tooltip label="Pause">
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                aria-label={`Pause upload of ${upload.title}`}
                onClick={() => pause(upload.key)}
              >
                <IconPlayerPause size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {upload.phase === 'paused' && (
            <Tooltip label="Resume">
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                aria-label={`Resume upload of ${upload.title}`}
                onClick={() => void resume(upload.key)}
              >
                <IconPlayerPlay size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {upload.phase === 'error' && upload.videoId && (
            <Tooltip label="Retry">
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                aria-label={`Retry upload of ${upload.title}`}
                onClick={() => void resume(upload.key)}
              >
                <IconRefresh size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {!active && (
            <Tooltip label={upload.phase === 'uploaded' ? 'Dismiss' : 'Cancel and dismiss'}>
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                aria-label={`Dismiss upload of ${upload.title}`}
                onClick={() => dismiss(upload.key)}
              >
                <IconX size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Group>
      <Progress
        value={upload.phase === 'uploaded' ? 100 : pct}
        color={upload.phase === 'error' ? 'coral' : upload.phase === 'uploaded' ? 'leaf' : 'sky'}
        striped={active}
        animated={upload.phase === 'uploading'}
        aria-label={`Upload progress for ${upload.title}`}
      />
      <Text
        size="xs"
        c={upload.phase === 'error' ? 'coral.8' : 'dimmed'}
        role={upload.phase === 'error' ? 'alert' : undefined}
      >
        {upload.phase === 'error'
          ? upload.error
          : `${PHASE[upload.phase]} · ${formatBytes(upload.bytesSent)} of ${formatBytes(upload.size)} (${pct}%)`}
      </Text>
    </Stack>
  );
}
